import { supabase } from '@/shared/lib/supabase';

/**
 * Dunning health — who is in the ladder and what happens to them next.
 *
 * Reads `v_admin_dunning_health` (Slice 2, 2026-09-10; BRD 2026-06-02 Slice
 * D-6, specified in June and never built). The view computes `next_step` and
 * `next_step_at` from the SAME predicates billing-cron STEP 2/3 use, so
 * "suspends tomorrow" on this page is true by construction. Owner-run view,
 * `WHERE is_staff(auth.uid())` — support may read it (D11: operational, not
 * financial).
 */

export type DunningNextStep =
  | 'grace'    // unpaid → grace at due_date
  | 'suspend'  // grace → suspended at grace_until
  | 'exempt'   // grace, but dunning_policy = no_suspend
  | 'held'     // active payment arrangement
  | 'review'   // books say suspended, WHM says live
  | 'collect'  // suspended on both sides — debt outstanding
  | 'none';

export interface DunningHealthRow {
  invoice_id: string;
  invoice_number: string;
  customer_id: string;
  customer_name: string | null;
  customer_link_id: string;
  cpanel_username: string;
  primary_domain: string | null;
  dunning_policy: string | null;
  hosting_state: string | null;
  hosting_desired_state: string | null;
  lifecycle_state: 'unpaid' | 'grace' | 'suspended';
  total: number;
  due_date: string;
  grace_until: string | null;
  suspended_at: string | null;
  arrangement_hold: boolean;
  next_step: DunningNextStep;
  next_step_at: string | null;
}

const COLS =
  'invoice_id, invoice_number, customer_id, customer_name, customer_link_id, cpanel_username, primary_domain, dunning_policy, hosting_state, hosting_desired_state, lifecycle_state, total, due_date, grace_until, suspended_at, arrangement_hold, next_step, next_step_at';

export const dunningHealthService = {
  async list(): Promise<DunningHealthRow[]> {
    const { data, error } = await supabase.from('v_admin_dunning_health').select(COLS);
    if (error) throw new Error(`Could not load dunning health: ${error.message}`);
    return ((data ?? []) as Array<Omit<DunningHealthRow, 'total'> & { total: string | number }>).map((r) => ({
      ...r,
      total: Number(r.total),
    }));
  },
};
